import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const FACE_API = process.env.REACT_APP_FACE_API || 'https://tribal-face-api.onrender.com';

// Steps:
//   1. usernames
//   2. DMCA takedown profile (stage_name + original_content_source)
//      — HARD REQUIRED: notices cannot render without these per
//        § 512(c)(3). Cannot be skipped.
//   3. face URL (optional)
//
// On mount, GET /api/settings/onboarding-status to skip any step
// already completed. Nordic-class creators (signed up before the
// DMCA profile step existed) will land straight on step 2.
export default function Onboarding({ creatorId, onComplete }) {
  const [step, setStep] = useState(1);
  const [usernames, setUsernames] = useState(['']);
  const [stageName, setStageName] = useState('');
  const [originalContentSource, setOriginalContentSource] = useState('');
  const [faceUrl, setFaceUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [faceStatus, setFaceStatus] = useState(null);
  const [error, setError] = useState(null);
  const [statusChecked, setStatusChecked] = useState(false);

  // Skip completed steps. Landing on the missing one prevents
  // "re-enter username you already set" purgatory for existing
  // incomplete creators.
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('tribal_token');
        const res = await axios.get(`${API}/api/settings/onboarding-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const s = res.data || {};
        if (!s.usernames_missing) {
          if ((s.missing_dmca_profile || []).length > 0) setStep(2);
          else setStep(3);
        }
      } catch (_) { /* fall through — starts at step 1 */ }
      setStatusChecked(true);
    })();
  }, []);

  const addUsername = () => setUsernames([...usernames, '']);
  const updateUsername = (i, val) => {
    const updated = [...usernames];
    updated[i] = val;
    setUsernames(updated);
  };
  const removeUsername = (i) => setUsernames(usernames.filter((_, idx) => idx !== i));

  const saveUsernames = async () => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem('tribal_token');
      await axios.post(`${API}/api/settings`, {
        usernames: usernames.filter(u => u.trim())
      }, { headers: { Authorization: `Bearer ${token}` } });
      setStep(2);
    } catch(e) {
      setError('Error saving usernames: ' + (e.response?.data?.error || e.message));
    }
    setLoading(false);
  };

  const saveDmcaProfile = async () => {
    const sn = stageName.trim();
    const ocs = originalContentSource.trim();
    if (!sn || !ocs) {
      setError('Both stage name and content source are required — DMCA notices cannot be sent without them (§ 512(c)(3)).');
      return;
    }
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem('tribal_token');
      await axios.post(`${API}/api/settings`, {
        stage_name: sn,
        original_content_source: ocs,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setStep(3);
    } catch(e) {
      setError('Error saving DMCA profile: ' + (e.response?.data?.error || e.message));
    }
    setLoading(false);
  };

  // Fire the onboarding leak scan then call onComplete. Any failure
  // in the scan-trigger is non-fatal — onboarding proceeds regardless.
  const finish = async () => {
    try {
      const token = localStorage.getItem('tribal_token');
      const res = await axios.post(`${API}/api/scans/onboarding`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.job_id) {
        localStorage.setItem('pending_scan_id', res.data.job_id);
      }
    } catch(e) {
      console.error('[ONBOARDING] scan trigger failed:', e.message);
    }
    onComplete();
  };

  const enrollFace = async () => {
    if (!faceUrl) { finish(); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${FACE_API}/v2/face/enroll`, {
        creator_id: creatorId,
        image_url: faceUrl
      });
      setFaceStatus(res.data);
      setTimeout(finish, 1500);
    } catch(e) {
      setFaceStatus({ success: false, error: e.message });
    }
    setLoading(false);
  };

  const inputStyle = {
    flex: 1, padding: '10px', borderRadius: '8px',
    background: '#1a1a1a', border: '1px solid #333',
    color: '#fff', fontSize: '14px'
  };
  const boxInputStyle = { ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '12px' };

  if (!statusChecked) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: '#111', borderRadius: '16px', padding: '40px',
        maxWidth: '520px', width: '90%', border: '1px solid #222'
      }}>
        {step === 1 && (
          <>
            <h2 style={{ color: '#a78bfa', marginBottom: '8px' }}>🛡️ Set Up Your Protection</h2>
            <p style={{ color: '#888', marginBottom: '24px', fontSize: '14px' }}>
              Add your usernames so we can scan for leaked content automatically every day.
            </p>
            {usernames.map((u, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <input
                  type="text"
                  placeholder={`Username ${i + 1} (e.g. TribalBBC)`}
                  value={u}
                  onChange={e => updateUsername(i, e.target.value)}
                  style={inputStyle}
                />
                {usernames.length > 1 && (
                  <button onClick={() => removeUsername(i)} style={{
                    padding: '10px', background: '#2e1a1a', border: 'none',
                    borderRadius: '8px', color: '#ef4444', cursor: 'pointer'
                  }}>✕</button>
                )}
              </div>
            ))}
            <button onClick={addUsername} style={{
              padding: '8px 16px', background: 'transparent', border: '1px solid #333',
              borderRadius: '8px', color: '#888', cursor: 'pointer', marginBottom: '24px', fontSize: '13px'
            }}>+ Add another username</button>
            <br />
            {error && <div style={{ color: '#ef4444', marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <button onClick={saveUsernames} disabled={loading} style={{
              width: '100%', padding: '12px', background: '#7c3aed', border: 'none',
              borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '15px'
            }}>
              {loading ? 'Saving...' : 'Continue →'}
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <h2 style={{ color: '#a78bfa', marginBottom: '8px' }}>📜 DMCA Takedown Profile</h2>
            <p style={{ color: '#888', marginBottom: '20px', fontSize: '14px' }}>
              These two fields appear on every takedown notice we file. Required by U.S. copyright law (§ 512(c)(3)) — we cannot send notices without them.
            </p>

            <label style={{ display: 'block', color: '#aaa', fontSize: 12, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>
              Stage name
            </label>
            <input
              type="text"
              placeholder="e.g. LeenaTheSwitch"
              value={stageName}
              onChange={e => setStageName(e.target.value)}
              style={boxInputStyle}
            />
            <div style={{ color: '#666', fontSize: 12, marginTop: -8, marginBottom: 16 }}>
              This is the name that appears on notices and (via the Lumen Database) becomes public. Use your public stage name — <b>not</b> your legal name.
            </div>

            <label style={{ display: 'block', color: '#aaa', fontSize: 12, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>
              Where your content is officially published
            </label>
            <input
              type="text"
              placeholder="OnlyFans.com/yourhandle"
              value={originalContentSource}
              onChange={e => setOriginalContentSource(e.target.value)}
              style={boxInputStyle}
            />
            <div style={{ color: '#666', fontSize: 12, marginTop: -8, marginBottom: 20 }}>
              The URL of the site(s) where you legitimately monetize your content. Hosts use this to verify the takedown is legitimate.
            </div>

            {error && <div style={{ color: '#ef4444', marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <button onClick={saveDmcaProfile} disabled={loading} style={{
              width: '100%', padding: '12px', background: '#7c3aed', border: 'none',
              borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '15px'
            }}>
              {loading ? 'Saving...' : 'Save & Continue →'}
            </button>
          </>
        )}

        {step === 3 && (
          <>
            <h2 style={{ color: '#a78bfa', marginBottom: '8px' }}>🧬 Face Protect (Coming soon · Optional)</h2>
            <p style={{ color: '#888', marginBottom: '24px', fontSize: '14px' }}>
              Paste a direct URL to your photo. When Face Protect ships, we'll use it to detect fake accounts using your face across social media.
            </p>
            <input
              type="text"
              placeholder="https://... (direct image URL)"
              value={faceUrl}
              onChange={e => setFaceUrl(e.target.value)}
              style={boxInputStyle}
            />
            {faceStatus && (
              <div style={{
                padding: '10px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px',
                background: faceStatus.success ? '#1a2e1a' : '#2e1a1a'
              }}>
                {faceStatus.success ? '✅ Face enrolled successfully!' : `❌ ${faceStatus.error}`}
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={finish} style={{
                flex: 1, padding: '12px', background: 'transparent', border: '1px solid #333',
                borderRadius: '8px', color: '#888', cursor: 'pointer', fontSize: '14px'
              }}>Skip for now</button>
              <button onClick={enrollFace} disabled={loading} style={{
                flex: 2, padding: '12px', background: '#7c3aed', border: 'none',
                borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '14px'
              }}>
                {loading ? 'Enrolling...' : 'Enroll Face & Finish'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
